/**
 * Picker для monorepo: если в репо несколько `.pflow/`-проектов,
 * пользователю надо выбрать, какой открыть.
 *
 * Логика на RepoPage:
 *   - один проект (с `.pflow/`) → авто-выбор, picker не показываем
 *   - несколько → блокирующий picker до выбора
 *   - текущий `path` (из URL) уже валиден → не показываем
 *
 * Этот компонент — только UI: владелец (RepoPage) грузит `/projects`
 * сам и решает, рендерить ли picker.
 */

import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { IconFolder, IconFolderRoot } from '@tabler/icons-react';

interface Props {
    projects: string[];
    onPick:   (path: string) => void;
}

export function ProjectPicker({ projects, onPick }: Props): JSX.Element {
    return (
        <Stack maw={520} mx="auto" mt="xl" gap="md">
            <Title order={3}>В репозитории несколько pflow-проектов</Title>
            <Text c="dimmed" fz="sm">
                Каждый помечен папкой `.pflow/`. Выбери, какой открыть. URL
                обновится — можно поделиться прямой ссылкой.
            </Text>
            <Stack gap="xs">
                {projects.map(p => (
                    <ProjectRow key={p || '__root__'} path={p} onPick={onPick} />
                ))}
            </Stack>
        </Stack>
    );
}

function ProjectRow({ path, onPick }: { path: string; onPick: (p: string) => void }): JSX.Element {
    const isRoot = path === '';
    return (
        <Card withBorder p="md" radius="md">
            <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    {isRoot ? <IconFolderRoot size={20} /> : <IconFolder size={20} />}
                    <Text ff="monospace" truncate>{isRoot ? '/ (корень репо)' : `/${path}`}</Text>
                </Group>
                <Button variant="default" size="xs" onClick={() => onPick(path)}>
                    Открыть
                </Button>
            </Group>
        </Card>
    );
}
